import { useCallback, useState } from 'react';

import type { FilterRangeValue, SelectedFilterValue } from '../types';
import { isNotEmpty, areNotEquivalent } from '../utils/selectedFilterValues';
import { toggleImmutable } from '../toggleImmutable';

export function useFilterState(initiallySelectedFilterValues: SelectedFilterValue[] = []) {
  const [selectedFilterId, setSelectedFilterId] = useState<string>();

  const [pendingFilterValues, setPendingFilterValues] = useState<SelectedFilterValue[]>(
    initiallySelectedFilterValues,
  );

  const setFilterRange = useCallback(
    ({ filterId, selectedRange }: { filterId: string; selectedRange?: FilterRangeValue }) => {
      setPendingFilterValues((current) => {
        const existingFilter = current.find((x) => x.filterId === filterId);

        const updatedFilter = existingFilter
          ? { ...existingFilter, selectedRange }
          : { filterId, selectedRange, selectedValues: [] };

        return current
          .filter((x) => x.filterId !== filterId)
          .concat(updatedFilter)
          .filter(isNotEmpty);
      });
    },
    [],
  );

  const toggleFilterValue = useCallback(
    ({
      filterId,
      valueId,
      isSingleSelection,
    }: {
      filterId: string;
      valueId: string;
      isSingleSelection?: boolean;
    }) => {
      setPendingFilterValues((current) => {
        const existingFilter = current.find((x) => x.filterId === filterId);

        const updatedFilter = existingFilter
          ? {
              ...existingFilter,
              selectedValues: isSingleSelection
                ? [valueId]
                : toggleImmutable(existingFilter.selectedValues, valueId),
            }
          : { filterId, selectedValues: [valueId] };

        return current
          .filter((x) => x.filterId !== filterId)
          .concat(updatedFilter)
          .filter(isNotEmpty);
      });
    },
    [],
  );

  const selectFilter = (filterId?: string) => {
    setPendingFilterValues(initiallySelectedFilterValues);
    setSelectedFilterId(filterId);
  };

  const hasChanges = areNotEquivalent(initiallySelectedFilterValues, pendingFilterValues);

  const activeFilterIds = initiallySelectedFilterValues.filter(isNotEmpty).map((x) => x.filterId);

  return {
    selectedFilterId,
    /** Selects a filter by its ID, resetting the selected values to the initial state */
    selectFilter,
    /** The currently selected filter values, including any pending changes */
    pendingFilterValues,
    /** Toggles a filter value for a specified filter */
    toggleFilterValue,
    /** Sets a range for a specified filter */
    setFilterRange,
    /** Indicates if the filter state has pending changes */
    hasChanges,
    /** Filters that are currently active, ignoring any pending changes */
    activeFilterIds,
  };
}
